import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import type { Currency } from "../entities/ledger/types";
import { getAccountDetail, retireAccount, updateAccount } from "../features/accounts/api";
import { debtSourceLabel, observedAt, readAccount } from "../features/accounts/accountMoney";
import { AccountStatePanel } from "../features/accounts/AccountStatePanel";
import { ReconcilePanel } from "../features/accounts/ReconcilePanel";
import { StatementPanel } from "../features/accounts/StatementPanel";
import { statementRange } from "../features/accounts/statementRange";
import { MovementComposer } from "../features/movement/MovementComposer";
import { useDashboard } from "../features/workspace/useDashboard";
import { humanError } from "../shared/api/client";
import { money } from "../shared/lib/format";
import { DateField } from "../shared/ui/DateField";
import { Modal } from "../shared/ui/Modal";
import { ErrorPanel, LoadingPanel } from "../shared/ui/PageState";
import { isoDay, periodPresets } from "./journal/period";

function formatDay(day: string): string {
  return new Intl.DateTimeFormat("en", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(`${day}T00:00:00Z`));
}

interface AccountPageProps {
  currency: Currency;
  onNotify: (message: string) => void;
}

// One account: what it holds, what moved, and — behind disclosures — the
// occasional tasks (record, rename, reconcile, archive) that would otherwise
// bury the statement under forms.
export function AccountPage({ currency, onNotify }: AccountPageProps) {
  const { accountId = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dashboard = useDashboard(currency);
  const accountDetail = useQuery({
    queryKey: ["account-detail", dashboard.data?.ids.book, accountId],
    queryFn: () => getAccountDetail(accountId),
    enabled: Boolean(dashboard.data?.ids.book && accountId),
  });
  const [details, setDetails] = useState<{ name: string; reference: string; displayValue: string } | null>(null);
  const relabel = useMutation({
    mutationFn: () => updateAccount(dashboard.data?.ids.book ?? "", accountId, details ?? { name: "", reference: "", displayValue: "" }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      onNotify(`${result.account.name} updated. Balances and history are unchanged.`);
    },
  });
  const retire = useMutation({
    mutationFn: () => retireAccount(dashboard.data?.ids.book ?? "", accountId),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      onNotify(`${result.account.name} archived: history kept, no new postings.`);
      navigate("/accounts", { replace: true });
    },
  });
  // Start far enough back to make the account's opening explicit and expose
  // older imported history. The user can narrow the range with Change the
  // period; a zero opening on a newly reset account is then unambiguous.
  const initialPeriod = { from: "2020-01-01", to: isoDay(new Date()) };
  const [fromDay, setFromDay] = useState(initialPeriod.from);
  const [toDay, setToDay] = useState(isoDay(new Date()));
  // Each editing task opens its own dialog; the page itself stays readable.
  const [dialog, setDialog] = useState<"" | "period" | "record" | "details" | "reconcile" | "state" | "archive">("");
  const [draftRange, setDraftRange] = useState({ from: initialPeriod.from, to: isoDay(new Date()) });

  if (!dashboard.data) {
    return (
      <section className="account-page view">
        <nav className="breadcrumbs" aria-label="Breadcrumb"><Link to="/accounts">Accounts</Link><span aria-hidden="true">/</span><span>Account</span></nav>
        {dashboard.isPending ? <LoadingPanel message="Loading this account…" /> : null}
        {dashboard.isError ? <ErrorPanel error={dashboard.error} onRetry={() => dashboard.refetch()} what="this account" /> : null}
      </section>
    );
  }

  const data = dashboard.data;
  if (accountDetail.isPending) return <section className="account-page view"><LoadingPanel message="Loading this account…" /></section>;
  if (accountDetail.isError) return <section className="account-page view"><ErrorPanel error={accountDetail.error} onRetry={() => accountDetail.refetch()} what="this account" /></section>;
  const detail = accountDetail.data;
  if (!detail) {
    return retire.isPending || retire.isSuccess ? null : <Navigate to="/accounts" replace />;
  }
  const account = {
    id: detail.id, bookId: detail.bookId, name: detail.name,
    currency: detail.currency, reference: detail.reference, displayValue: detail.displayValue,
    displayLabel: detail.displayLabel, archived: detail.archived,
  } as typeof data.accounts[number];
  const balance = {
    account, nativeBalance: detail.nativeBalance, carryingBookValue: detail.carryingBookValue,
    reportingValue: detail.reportingValue, position: detail.position, state: detail.state ?? undefined,
    debtAttribution: detail.debtAttribution, linkedBorrowings: detail.linkedBorrowings,
  };
  const range = fromDay <= toDay ? statementRange(fromDay, toDay) : null;
  const form = details ?? { name: account.name, reference: account.reference ?? "", displayValue: account.displayValue ?? "" };
  const holds = Boolean(balance && balance.nativeBalance.minor !== 0);
  const reading = readAccount(account, balance);

  return (
    <section className="account-page view" aria-labelledby="account-title">
      <nav className="breadcrumbs" aria-label="Breadcrumb">
        <Link to="/accounts">Accounts</Link><span aria-hidden="true">/</span><span>{account.name}</span>
      </nav>
      <div className="page-heading">
        <div>
          <p className="eyebrow">{account.currency}{account.displayLabel ? ` · ${account.displayLabel}` : ""}</p>
          <h1 id="account-title">{account.name}</h1>
          {account.reference ? <p className="lede account-reference-full" title={account.reference}>{account.reference}</p> : null}
        </div>
        <div className="heading-actions">
          <Link className="secondary-button" to={`/journal?account=${encodeURIComponent(account.id)}`}>Open in journal</Link>
        </div>
      </div>

      <div className="summary-grid">
        <article className="panel balance-card">
          <div className="card-label">
            <span>Balance now</span><span className="account-chip">{account.currency}</span>
          </div>
          <div className="balance-amount">{money(reading.balance.minor, reading.balance.currency)}</div>
          {reading.book
            ? <p>{money(reading.book.value.minor, reading.book.value.currency)} {reading.book.owed ? "owed" : "held"} in the book currency.</p>
            : reading.bookUnavailable
            ? <p>No rate is available to value this account in the book currency.</p>
            : <p>What this account holds, from its own history.</p>}
          {(() => {
            const reporting = balance.position?.reporting;
            if (!reporting || reporting.held.currency === account.currency) return null;
            return reporting.owed.minor > 0
              ? <p>≈ {money(reporting.owed.minor, reporting.owed.currency)} owed, valued in the reporting currency today.</p>
              : <p>≈ {money(reporting.held.minor, reporting.held.currency)} valued in the reporting currency today.</p>;
          })()}
          {reading.debt || reading.available || reading.borrowings.length > 0 ? (
            <dl className="account-extras wide">
              {reading.debt ? (
                <div>
                  <dt>Account debt</dt>
                  <dd className="owed">
                    {money(reading.debt.minor, reading.debt.currency)}
                    <small>
                      What this account owes by its own history.
                      {reading.unattributed ? " No Borrowing answers for it yet, so net worth counts it as unattributed debt." : " Net worth counts the linked Borrowing's debt instead."}
                    </small>
                  </dd>
                </div>
              ) : null}
              {reading.borrowings.map((linked) => (
                <div key={linked.agreementId}>
                  <dt>Borrowing debt · {linked.creditor}</dt>
                  <dd>
                    {linked.debt ? money(linked.debt.minor, linked.debt.currency) : <span className="unknown">Unknown</span>}
                    <small>{linked.debt ? `The credit obligation, ${debtSourceLabel(linked.debtSource)}.` : "The agreement could not be replayed."}</small>
                  </dd>
                </div>
              ))}
              {reading.available ? (
                <div>
                  <dt>Available to spend</dt>
                  <dd>
                    {money(reading.available.amount.minor, reading.available.amount.currency)}
                    <small>
                      Reported by the provider{reading.available.observedAt ? `, observed ${observedAt(reading.available.observedAt)}` : ""}. Unused credit included; not the account balance.
                    </small>
                  </dd>
                </div>
              ) : null}
            </dl>
          ) : null}
          <button className="panel-link" type="button" onClick={() => setDialog("state")}>
            {reading.available ? "Update what the bank reports" : "Record what the bank reports"}
          </button>
        </article>
        <article className="panel balance-card range-card">
          <div className="card-label"><span>Statement period</span></div>
          <div className="balance-amount range-value">{formatDay(fromDay)} — {formatDay(toDay)}</div>
          <p>Both days included. Corrections stay visible next to the original entry.</p>
          <button className="panel-link" type="button" onClick={() => { setDraftRange({ from: fromDay, to: toDay }); setDialog("period"); }}>Change the period</button>
        </article>
      </div>

      {range ? <StatementPanel bookID={data.ids.book} accounts={data.accounts} accountID={account.id} from={range.from} to={range.to} onNotify={onNotify} /> : null}

      <div className="account-tasks">
        <button className="primary-button compact" type="button" onClick={() => setDialog("record")}>
          <span>Record money</span><span aria-hidden="true">→</span>
        </button>
        {/* A card's debt is declared the same way an asset's balance is: the
            journal records the correction. Withholding it from liabilities is
            what left a card with no way to state what it owes. */}
        <button className="secondary-button" type="button" onClick={() => setDialog("reconcile")}>
          Reconcile
        </button>
        <button className="secondary-button" type="button" onClick={() => { setDetails(null); setDialog("details"); }}>Account details</button>
        <button className="secondary-button danger" type="button" onClick={() => setDialog("archive")}>Archive account</button>
      </div>

      <Modal
        open={dialog === "period"} onClose={() => setDialog("")} title="Statement period" subtitle={account.name}
        footer={<>
          <button className="secondary-button" type="button" onClick={() => setDialog("")}>Cancel</button>
          <button className="primary-button compact" type="button" disabled={draftRange.from > draftRange.to}
            onClick={() => { setFromDay(draftRange.from); setToDay(draftRange.to); setDialog(""); }}>
            <span>Apply</span><span aria-hidden="true">→</span>
          </button>
        </>}
      >
        <div className="field-grid">
          <DateField label="From" value={draftRange.from} max={draftRange.to} rangeFrom={draftRange.to} onChange={(from) => setDraftRange({ ...draftRange, from })} />
          <DateField label="To" value={draftRange.to} min={draftRange.from} rangeFrom={draftRange.from} onChange={(to) => setDraftRange({ ...draftRange, to })} />
        </div>
        <div className="field">
          <span className="field-label">Quick ranges</span>
          <div className="chip-group period-presets">
            {periodPresets.map((preset) => <button className="chip" type="button" key={preset.label} onClick={() => setDraftRange(preset.value())}>{preset.label}</button>)}
          </div>
        </div>
        {draftRange.from > draftRange.to ? <p className="form-error" role="alert">The period must start before it ends.</p> : null}
      </Modal>

      <Modal
        open={dialog === "record"} onClose={() => setDialog("")} title="Record money"
        subtitle={`Every operation starts from ${account.name}.`}>
        <MovementComposer ids={data.ids} currency={account.currency} accounts={data.accounts}
          onNotify={(message) => { onNotify(message); setDialog(""); }} fixedAccountID={account.id}
          categories={data.categories} bare />
      </Modal>

      <Modal
        open={dialog === "details"} onClose={() => setDialog("")} title="Account details" subtitle={account.name}
        footer={<>
          <button className="secondary-button" type="button" onClick={() => setDialog("")}>Cancel</button>
          <button className="primary-button compact" type="submit" form="account-details-form" disabled={relabel.isPending}>
            <span>{relabel.isPending ? "Saving…" : "Save details"}</span><span aria-hidden="true">→</span>
          </button>
        </>}
      >
        <form id="account-details-form" className="stacked-form" aria-label="Account details"
          onSubmit={(event) => { event.preventDefault(); if (form.name.trim()) relabel.mutate(); }}>
          <label>Name<input value={form.name} onChange={(event) => setDetails({ ...form, name: event.target.value })} required autoFocus /></label>
          <label>Reference <small>IBAN or account number</small><input value={form.reference} onChange={(event) => setDetails({ ...form, reference: event.target.value })} placeholder="GE27TB7654321000000003" /></label>
          <label>Display value <small>card mask or tag; empty masks the reference</small><input value={form.displayValue} onChange={(event) => setDetails({ ...form, displayValue: event.target.value })} placeholder="#1" /></label>
          {relabel.error ? <p className="form-error" role="alert">{humanError(relabel.error)}</p> : null}
        </form>
      </Modal>

      <Modal
        open={dialog === "reconcile"} onClose={() => setDialog("")} title="Reconcile"
        subtitle={`Match ${account.name} to the balance your bank shows.`}>
        <ReconcilePanel bookID={data.ids.book} account={account} balance={balance} onNotify={onNotify} bare />
      </Modal>

      <Modal
        open={dialog === "state"} onClose={() => setDialog("")} title="What the bank reports" subtitle={account.name}
        footer={<>
          <button className="secondary-button" type="button" onClick={() => setDialog("")}>Cancel</button>
          <button className="primary-button compact" type="submit" form={`account-state-${account.id}`}>
            <span>Record it</span><span aria-hidden="true">→</span>
          </button>
        </>}
      >
        <AccountStatePanel bookID={data.ids.book} account={account} balance={balance}
          onDone={(message) => { onNotify(message); setDialog(""); }} />
      </Modal>
      <Modal
        open={dialog === "archive"} onClose={() => setDialog("")} title="Archive this account" subtitle={account.name}
        footer={<>
          <button className="secondary-button" type="button" onClick={() => setDialog("")}>Cancel</button>
          <button className="primary-button compact danger" type="button" disabled={retire.isPending || holds} onClick={() => retire.mutate()}>
            <span>{retire.isPending ? "Archiving…" : "Archive account"}</span><span aria-hidden="true">→</span>
          </button>
        </>}
      >
        <p className="panel-note">
          {holds
            ? `${account.name} still ${reading.debt ? `owes ${money(reading.debt.minor, reading.debt.currency)}` : `holds ${money(reading.balance.minor, reading.balance.currency)}`}. Transfer it out or reconcile to zero before archiving.`
            : "The account disappears from pickers and lists. Its statement stays readable and every posting stays in the journal. Nothing is erased."}
        </p>
        {retire.error ? <p className="form-error" role="alert">{humanError(retire.error)}</p> : null}
      </Modal>

    </section>
  );
}
